import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { FiltersPopover } from '@/shared/components/custom/filters-popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import type { MyTasksParams } from '../types';

interface ProjectOption {
  id: string;
  name: string;
}

interface MySummaryTasksFiltersProps {
  filters: Omit<MyTasksParams, 'page' | 'pageSize'>;
  onChange: (filters: Omit<MyTasksParams, 'page' | 'pageSize'>) => void;
  projectOptions: ProjectOption[];
}

const UNSET = '__unset__';

export function MySummaryTasksFilters({
  filters,
  onChange,
  projectOptions,
}: MySummaryTasksFiltersProps) {
  const { t } = useTranslation('personalSummary');

  const activeCount =
    (filters.priority ? 1 : 0) + (filters.projectId ? 1 : 0) + (filters.statusType ? 1 : 0);

  return (
    <FiltersPopover
      activeCount={activeCount}
      label={t('tasks.filters.button')}
      data-testid="personal-summary-tasks-filters-btn"
    >
      <FiltersPopover.Content>
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{t('tasks.filters.status')}</p>
          <Select
            value={filters.statusType ?? UNSET}
            onValueChange={(v) =>
              onChange({
                ...filters,
                statusType: v === UNSET ? undefined : (v as MyTasksParams['statusType']),
              })
            }
          >
            <SelectTrigger
              className="h-8 text-xs"
              data-testid="personal-summary-tasks-filter-status"
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNSET}>—</SelectItem>
              <SelectItem value="Todo">{t('statusType.Todo')}</SelectItem>
              <SelectItem value="InProgress">{t('statusType.InProgress')}</SelectItem>
              <SelectItem value="Done">{t('statusType.Done')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{t('tasks.filters.priority')}</p>
          <Select
            value={filters.priority ?? UNSET}
            onValueChange={(v) => onChange({ ...filters, priority: v === UNSET ? undefined : v })}
          >
            <SelectTrigger
              className="h-8 text-xs"
              data-testid="personal-summary-tasks-filter-priority"
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNSET}>—</SelectItem>
              <SelectItem value="None">{t('priority.None')}</SelectItem>
              <SelectItem value="Low">{t('priority.Low')}</SelectItem>
              <SelectItem value="Medium">{t('priority.Medium')}</SelectItem>
              <SelectItem value="High">{t('priority.High')}</SelectItem>
              <SelectItem value="Urgent">{t('priority.Urgent')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {projectOptions.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              {t('tasks.filters.project')}
            </p>
            <Select
              value={filters.projectId ?? UNSET}
              onValueChange={(v) =>
                onChange({ ...filters, projectId: v === UNSET ? undefined : v })
              }
            >
              <SelectTrigger
                className="h-8 text-xs"
                data-testid="personal-summary-tasks-filter-project"
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET}>—</SelectItem>
                {projectOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {activeCount > 0 && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="w-full"
            onClick={() => onChange({})}
            data-testid="personal-summary-tasks-filters-clear"
          >
            {t('tasks.filters.clearFilters')}
          </Button>
        )}
      </FiltersPopover.Content>
    </FiltersPopover>
  );
}
